/**
 * TalCalculator — Batch #14.231
 *
 * Calculatrice d'augmentation de loyer (style TAL Québec).
 * 100 % frontend : loyer actuel + % d'augmentation → nouveau loyer,
 * impact mensuel et annuel. Carte repliable, réutilisée :
 *   - Portail immeuble (vue immeuble)
 *   - Gestion admin (mirror Vue portail)
 */
import { useState } from "react";
import { Calculator, ChevronDown, ChevronUp } from "lucide-react";
import { fmtCAD as money } from "@/lib/format";


export function TalCalculator({ testIdPrefix = "tal", defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const [rent, setRent] = useState("");
  const [pct, setPct] = useState("");

  const rentN = parseFloat(rent) || 0;
  const pctN = parseFloat(pct) || 0;
  const newRent = rentN * (1 + pctN / 100);
  const deltaMonth = newRent - rentN;
  const hasResult = rentN > 0 && pct !== "";

  return (
    <div
      className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden"
      data-testid={`${testIdPrefix}-calculator`}
    >
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition"
        data-testid={`${testIdPrefix}-calculator-toggle`}
      >
        <span className="flex items-center gap-2 text-sm font-bold text-slate-800">
          <span className="w-8 h-8 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center">
            <Calculator className="w-4 h-4" />
          </span>
          Calculatrice d'augmentation de loyer (TAL)
        </span>
        {open ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="text-[11px] font-semibold text-slate-600">
              Loyer actuel ($/mois)
              <input
                type="number" min="0" step="1" value={rent}
                onChange={(e) => setRent(e.target.value)}
                placeholder="ex : 1000"
                className="w-full mt-1 px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:border-blue-400"
                data-testid={`${testIdPrefix}-calculator-rent`}
              />
            </label>
            <label className="text-[11px] font-semibold text-slate-600">
              Augmentation (%)
              <input
                type="number" min="0" step="0.1" value={pct}
                onChange={(e) => setPct(e.target.value)}
                placeholder="ex : 5.9"
                className="w-full mt-1 px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:border-blue-400"
                data-testid={`${testIdPrefix}-calculator-pct`}
              />
            </label>
          </div>

          {hasResult && (
            <div className="bg-blue-50 border border-blue-100 rounded-xl p-3 grid grid-cols-3 gap-2 text-center">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-blue-500 font-bold">Nouveau loyer</div>
                <div className="text-base font-bold text-blue-900" data-testid={`${testIdPrefix}-calculator-result`}>
                  {money(newRent)}
                </div>
                <div className="text-[10px] text-blue-400">/mois</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-blue-500 font-bold">Impact mensuel</div>
                <div className="text-base font-bold text-blue-900">+{money(deltaMonth)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-blue-500 font-bold">Impact annuel</div>
                <div className="text-base font-bold text-blue-900">+{money(deltaMonth * 12)}</div>
              </div>
            </div>
          )}

          <p className="text-[10px] text-slate-400">
            Indiquez le pourcentage applicable publié chaque année par le Tribunal administratif du logement
            (TAL). Cet outil est indicatif — le calcul officiel du TAL peut tenir compte des taxes, travaux et
            autres facteurs.
          </p>
        </div>
      )}
    </div>
  );
}

export default TalCalculator;
